import React from 'react';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import Tooltip from '@splunk/react-ui/Tooltip';
import { sprintf } from '@splunk/ui-utils/format';
import { _ } from '@splunk/ui-utils/i18n';
import { RemoveButton, type RemoveButtonProps } from './RemoveButton';
import { EditButton, type EditButtonProps } from './EditButton';
import { ItemName, type ItemNameProps } from './ItemName';
import {
    VisibilityButton,
    type VisibilityButtonProps,
} from './VisibilityButton';

interface ItemProps {
    dataTest?: string;
    dataTestId?: string;
    isItemClickable?: boolean;
}

const Item = styled.div.attrs<ItemProps>(({ dataTest, dataTestId }) => ({
    'data-test': dataTest,
    'data-test-id': dataTestId,
}))<ItemProps>`
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    color: ${variables.contentColorDefault};
    line-height: ${variables.inputHeight};
    height: ${variables.inputHeight};
    &:hover {
        cursor: ${(prop) => (prop.isItemClickable ? 'pointer' : 'default')};
    }
`;

const IconTooltip = styled(Tooltip).attrs(() => ({
    'data-test': 'icon-tooltip',
}))`
    display: flex;
    align-items: center;
    flex-grow: 0;
    min-width: ${variables.inputHeight};
`;

export interface EditableListItemProps
    extends ItemNameProps,
        EditButtonProps,
        RemoveButtonProps,
        VisibilityButtonProps {
    'data-test'?: string;
    'data-test-id'?: string;
}

export const EditableListItem = ({
    name,
    visibilityState,
    onToggleVisibility,
    onItemClick,
    onEditClick,
    onRemoveClick,
    RemoveIcon,
    editButtonRef,
    'data-test': dataTest = 'editable-list-item',
    'data-test-id': dataTestId,
    isUsed = true,
}: EditableListItemProps) => {
    const hasVisibilityToggle = !!visibilityState && !!onToggleVisibility;

    return (
        <Item dataTest={dataTest} dataTestId={dataTestId}>
            <ItemName
                name={name}
                isUsed={isUsed}
                hasVisibilityToggle={hasVisibilityToggle}
                onItemClick={onItemClick}
            />
            <IconTooltip
                content={sprintf(_(`Edit %(name)s`), { name })}
                contentRelationship="label"
            >
                <EditButton
                    onEditClick={onEditClick}
                    editButtonRef={editButtonRef}
                />
            </IconTooltip>
            <VisibilityButton
                visibilityState={visibilityState}
                onToggleVisibility={onToggleVisibility}
            />
            <IconTooltip
                content={sprintf(_(`Remove %(name)s`), { name })}
                contentRelationship="label"
            >
                <RemoveButton
                    onRemoveClick={onRemoveClick}
                    RemoveIcon={RemoveIcon}
                />
            </IconTooltip>
        </Item>
    );
};
